import { GridFilterModel } from '@mui/x-data-grid';

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Case-insensitive MongoDB regex condition from an already-built pattern.
const ci = (pattern: string) => ({ $regex: pattern, $options: 'i' });

/**
 * Translates a MUI DataGrid `GridFilterModel` into a raw MongoDB `where`
 * object that the backend controllers merge into their `find` query.
 *
 * Only text / number operators are handled — date columns are intentionally
 * left non-filterable in the grids (sorting covers them).
 *
 * Returns `undefined` when there is nothing usable to filter on.
 */
export const buildMongoWhere = (
  filterModel?: GridFilterModel,
): Record<string, unknown> | undefined => {
  if (!filterModel?.items?.length) return undefined;

  const conditions: Record<string, unknown>[] = [];

  for (const { field, operator, value } of filterModel.items) {
    if (!field || !operator) continue;

    // Operators that don't need a value.
    if (operator === 'isEmpty') {
      conditions.push({ [field]: { $in: [null, ''] } });
      continue;
    }
    if (operator === 'isNotEmpty') {
      conditions.push({ [field]: { $nin: [null, ''] } });
      continue;
    }

    // Every other operator requires a value.
    if (value === undefined || value === null || value === '') continue;

    switch (operator) {
      // --- string operators ---
      case 'contains':
        conditions.push({ [field]: ci(escapeRegExp(String(value))) });
        break;
      case 'equals':
        conditions.push({ [field]: ci(`^${escapeRegExp(String(value))}$`) });
        break;
      case 'startsWith':
        conditions.push({ [field]: ci(`^${escapeRegExp(String(value))}`) });
        break;
      case 'endsWith':
        conditions.push({ [field]: ci(`${escapeRegExp(String(value))}$`) });
        break;
      case 'isAnyOf':
        if (Array.isArray(value) && value.length > 0) {
          conditions.push({ [field]: { $in: value } });
        }
        break;

      // --- number operators ---
      case '=':
        conditions.push({ [field]: Number(value) });
        break;
      case '!=':
        conditions.push({ [field]: { $ne: Number(value) } });
        break;
      case '>':
        conditions.push({ [field]: { $gt: Number(value) } });
        break;
      case '>=':
        conditions.push({ [field]: { $gte: Number(value) } });
        break;
      case '<':
        conditions.push({ [field]: { $lt: Number(value) } });
        break;
      case '<=':
        conditions.push({ [field]: { $lte: Number(value) } });
        break;

      default:
        break;
    }
  }

  if (conditions.length === 0) return undefined;
  if (conditions.length === 1) return conditions[0];

  const logic = filterModel.logicOperator === 'or' ? '$or' : '$and';
  return { [logic]: conditions };
};
